import React from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import { IUserCard } from '@share/common-types';
import { GetCreditCardImage } from '@utils';

import styles from './styles.module.scss';

const MAX_SNAPSHOT_CARDS = 2;

interface IProps {
  cards: IUserCard[];
  paymentPath: string;
}

const getExpiry = (card: IUserCard): string => {
  const value = card.expireDate || '';
  return value.length >= 4 ? `${value.substring(0, 2)}/${value.slice(-2)}` : '';
};

/** A glance at saved payment cards on Overview, linking to Payment methods to manage them. */
export const PaymentSnapshot = ({ cards, paymentPath }: IProps): React.ReactElement => {
  const intl = useIntl();
  const shown = cards.slice(0, MAX_SNAPSHOT_CARDS);
  const more = cards.length - shown.length;

  return (
    <section className={styles.card} aria-labelledby="account-hub-payment-snapshot-title">
      <div className={styles.cardHeader}>
        <h2 id="account-hub-payment-snapshot-title" className={styles.label}>
          {intl.formatMessage({ id: 'my.account.hub.section.payment' })}
        </h2>
        <Link to={paymentPath} className={styles.cardLink}>
          {intl.formatMessage({ id: cards.length ? 'my.account.hub.payment.manage' : 'my.account.hub.payment.add' })}
        </Link>
      </div>

      {shown.length ? (
        <ul className={styles.paymentChips}>
          {shown.map(card => (
            <li key={card.id || card.cardNumber} className={styles.paymentChip}>
              <span className={styles.paymentBrand}>{GetCreditCardImage(card.cardType as string, true)}</span>
              <span className={styles.paymentNumber}>•••• {card.cardNumber?.slice(-4)}</span>
              {getExpiry(card) ? (
                <span className={styles.paymentExpiry}>
                  {intl.formatMessage({ id: 'my.account.hub.payment.expires' }, { date: getExpiry(card) })}
                </span>
              ) : null}
            </li>
          ))}
          {more > 0 ? (
            <li className={styles.paymentMore}>
              {intl.formatMessage({ id: 'my.account.hub.payment.more' }, { count: more })}
            </li>
          ) : null}
        </ul>
      ) : (
        <Link to={paymentPath} className={styles.paymentAdd}>
          <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
          {intl.formatMessage({ id: 'my.account.hub.payment.snapshot_empty' })}
        </Link>
      )}
    </section>
  );
};
